import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { suppliersAPI, scopesAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ArrowLeft, Save, Building2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const APPROVAL_STATUSES = [
  'Created',
  'On Hold',
  'Approved',
  'No Longer Needed',
  'Under re-evaluation'
];

export default function SupplierFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user, isAdmin, canEditMetadata, canMakeEntries } = useAuth();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [scopes, setScopes] = useState([]);
  const [newScopeName, setNewScopeName] = useState('');

  const [formData, setFormData] = useState({
    supplier_code: '',
    supplier_name: '',
    supplier_type: 'AS',
    address_1: '',
    address_2: '',
    city: '',
    state_province: '',
    zip_code: '',
    country: '',
    contact_person: '',
    phone_number: '',
    email: '',
    approval_status: 'Created',
    criteria_quality_cert: false,
    quality_cert_number: '',
    quality_cert_expiry: '',
    criteria_authorized_distributor: false,
    auth_distributor_cert: '',
    auth_distributor_expiry: '',
    criteria_onsite_visit: false,
    onsite_visit_date: '',
    onsite_visit_by: '',
    criteria_customer_mandate: false,
    customer_mandate_details: '',
    criteria_sole_source_oem: false,
    criteria_sample_approval: false,
    sample_po_number: '',
    sample_po_date: '',
    scope_of_approval: [],
    scope_of_approval_text: '',
    criteria_approval_text: '',
    dpd_mbd_approved: false,
    reevaluation_date: '',
  });

  useEffect(() => {
    loadScopes();
    if (isEdit) {
      loadSupplier();
    }
  }, [id]);

  const loadScopes = async () => {
    try {
      const res = await scopesAPI.list();
      setScopes(res.data);
    } catch (err) {
      console.error('Failed to load scopes');
    }
  };

  const loadSupplier = async () => {
    try {
      const res = await suppliersAPI.get(id);
      setFormData({
        ...formData,
        ...res.data,
        scope_of_approval: res.data.scope_of_approval || [],
        scope_of_approval_text: res.data.scope_of_approval_text || '',
        criteria_approval_text: res.data.criteria_approval_text || '',
      });
    } catch (err) {
      toast.error('Failed to load supplier');
      navigate('/suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleScopeToggle = (scopeId) => {
    setFormData(prev => ({
      ...prev,
      scope_of_approval: prev.scope_of_approval.includes(scopeId)
        ? prev.scope_of_approval.filter(s => s !== scopeId)
        : [...prev.scope_of_approval, scopeId]
    }));
  };

  const handleAddScope = async () => {
    if (!newScopeName.trim()) return;
    try {
      const res = await scopesAPI.create({ name: newScopeName.trim() });
      setScopes(prev => [...prev, res.data]);
      setNewScopeName('');
      toast.success('Scope added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add scope');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_name || !formData.address_1 || !formData.city || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate scope of approval is mandatory for AS suppliers only
    if (formData.supplier_type !== 'NON-AS' && (!formData.scope_of_approval || formData.scope_of_approval.length === 0)) {
      toast.error('Scope of Approval is mandatory. Please select at least one scope.');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await suppliersAPI.update(id, formData);
        toast.success('Supplier updated');
      } else {
        await suppliersAPI.create(formData);
        toast.success('Supplier created');
      }
      navigate('/suppliers');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save supplier');
    } finally {
      setSaving(false);
    }
  };

  // Both manager and app_user can create/edit suppliers
  const canCreate = canMakeEntries('supplier_mgmt');

  if (!canCreate) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">You don't have permission to create suppliers.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/suppliers')}
                className="text-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="h-4 w-px bg-slate-300" />
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600" />
                <h1 className="text-lg font-semibold text-[#0F172A]">
                  {isEdit ? 'Edit Supplier' : 'New Supplier'}
                </h1>
              </div>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Supplier'}
            </Button>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Basic Information</CardTitle>
              <CardDescription className="text-xs">Supplier identification and contact details</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Supplier Code (SAP)</Label>
                <Input
                  value={formData.supplier_code}
                  onChange={(e) => handleChange('supplier_code', e.target.value)}
                  placeholder="Enter SAP supplier code"
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Supplier Name *</Label>
                <Input
                  value={formData.supplier_name}
                  onChange={(e) => handleChange('supplier_name', e.target.value)}
                  placeholder="Enter supplier name"
                  required
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Supplier Type *</Label>
                <div className="flex gap-3">
                  <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all ${formData.supplier_type !== 'NON-AS' ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <input type="radio" name="supplier_type" value="AS" checked={formData.supplier_type !== 'NON-AS'} onChange={() => handleChange('supplier_type', 'AS')} className="accent-emerald-600" data-testid="supplier-type-as" />
                    <div>
                      <span className="text-sm font-medium">AS (Standard)</span>
                      <p className="text-[10px] text-muted-foreground">Full approval, criteria, scope, reevaluation</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all ${formData.supplier_type === 'NON-AS' ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <input type="radio" name="supplier_type" value="NON-AS" checked={formData.supplier_type === 'NON-AS'} onChange={() => handleChange('supplier_type', 'NON-AS')} className="accent-amber-600" data-testid="supplier-type-non-as" />
                    <div>
                      <span className="text-sm font-medium">NON-AS (General Purpose)</span>
                      <p className="text-[10px] text-muted-foreground">No approval process, no reevaluation, direct receive</p>
                    </div>
                  </label>
                </div>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Address Line 1 *</Label>
                <Input
                  value={formData.address_1}
                  onChange={(e) => handleChange('address_1', e.target.value)}
                  placeholder="Street address"
                  required
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Address Line 2</Label>
                <Input
                  value={formData.address_2}
                  onChange={(e) => handleChange('address_2', e.target.value)}
                  placeholder="Building, floor, suite (optional)"
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">City *</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="City"
                  required
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">State/Province</Label>
                <Input
                  value={formData.state_province}
                  onChange={(e) => handleChange('state_province', e.target.value)}
                  placeholder="State or province"
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">ZIP/Postal Code</Label>
                <Input
                  value={formData.zip_code}
                  onChange={(e) => handleChange('zip_code', e.target.value)}
                  placeholder="ZIP code"
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Country *</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  placeholder="Country"
                  required
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Contact Person</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => handleChange('contact_person', e.target.value)}
                  placeholder="Primary contact name"
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Phone Number</Label>
                <Input
                  value={formData.phone_number}
                  onChange={(e) => handleChange('phone_number', e.target.value)}
                  placeholder="+91 XXXXX XXXXX"
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="supplier@example.com"
                  className="h-9"
                />
              </div>
            </CardContent>
          </Card>

          {/* Approval Status — only for AS suppliers */}
          {formData.supplier_type !== 'NON-AS' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Approval Status</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <Select value={formData.approval_status} onValueChange={(v) => handleChange('approval_status', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APPROVAL_STATUSES.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Re-evaluation Date</Label>
                <Input
                  type="date"
                  value={formData.reevaluation_date}
                  onChange={(e) => handleChange('reevaluation_date', e.target.value)}
                  className="h-9"
                />
                <p className="text-[10px] text-slate-400">Auto-calculated as approval date + 1 year if left blank</p>
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <Checkbox
                  checked={formData.dpd_mbd_approved}
                  onCheckedChange={(c) => handleChange('dpd_mbd_approved', c)}
                />
                <Label className="text-xs">DPD/MBD Approved</Label>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Criteria for Approval — only for AS suppliers */}
          {formData.supplier_type !== 'NON-AS' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Criteria for Approval</CardTitle>
              <CardDescription className="text-xs">Select applicable criteria and provide supporting details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quality System Certification */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.criteria_quality_cert}
                    onCheckedChange={(c) => handleChange('criteria_quality_cert', c)}
                  />
                  <Label className="text-sm font-medium">Quality System Certification</Label>
                </div>
                {formData.criteria_quality_cert && (
                  <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Certificate Number</Label>
                      <Input
                        value={formData.quality_cert_number}
                        onChange={(e) => handleChange('quality_cert_number', e.target.value)}
                        placeholder="Certificate #"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Expiry Date</Label>
                      <Input
                        type="date"
                        value={formData.quality_cert_expiry}
                        onChange={(e) => handleChange('quality_cert_expiry', e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Authorized Distributor */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.criteria_authorized_distributor}
                    onCheckedChange={(c) => handleChange('criteria_authorized_distributor', c)}
                  />
                  <Label className="text-sm font-medium">Authorized Distributor (listed in manufacturer's web)</Label>
                </div>
                {formData.criteria_authorized_distributor && (
                  <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Certificate/Reference</Label>
                      <Input
                        value={formData.auth_distributor_cert}
                        onChange={(e) => handleChange('auth_distributor_cert', e.target.value)}
                        placeholder="Reference #"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Expiry Date</Label>
                      <Input
                        type="date"
                        value={formData.auth_distributor_expiry}
                        onChange={(e) => handleChange('auth_distributor_expiry', e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* On-site Visit */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.criteria_onsite_visit}
                    onCheckedChange={(c) => handleChange('criteria_onsite_visit', c)}
                  />
                  <Label className="text-sm font-medium">On-site Visit</Label>
                </div>
                {formData.criteria_onsite_visit && (
                  <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Date of Visit</Label>
                      <Input
                        type="date"
                        value={formData.onsite_visit_date}
                        onChange={(e) => handleChange('onsite_visit_date', e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Visited By</Label>
                      <Input
                        value={formData.onsite_visit_by}
                        onChange={(e) => handleChange('onsite_visit_by', e.target.value)}
                        placeholder="Name of visitor"
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Customer Mandate */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.criteria_customer_mandate}
                    onCheckedChange={(c) => handleChange('criteria_customer_mandate', c)}
                  />
                  <Label className="text-sm font-medium">Customer Mandate</Label>
                </div>
                {formData.criteria_customer_mandate && (
                  <div className="pl-6">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Customer Details</Label>
                      <Input
                        value={formData.customer_mandate_details}
                        onChange={(e) => handleChange('customer_mandate_details', e.target.value)}
                        placeholder="Which customer (will link to Contract Management later)"
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Sole Source / OEM */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.criteria_sole_source_oem}
                    onCheckedChange={(c) => handleChange('criteria_sole_source_oem', c)}
                  />
                  <Label className="text-sm font-medium">Sole Source / OEM Status</Label>
                </div>
              </div>

              {/* Sample Approval */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.criteria_sample_approval}
                    onCheckedChange={(c) => handleChange('criteria_sample_approval', c)}
                  />
                  <Label className="text-sm font-medium">As per Sample Approval</Label>
                </div>
                {formData.criteria_sample_approval && (
                  <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Purchase Order Number</Label>
                      <Input
                        value={formData.sample_po_number}
                        onChange={(e) => handleChange('sample_po_number', e.target.value)}
                        placeholder="PO #"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">PO Date</Label>
                      <Input
                        type="date"
                        value={formData.sample_po_date}
                        onChange={(e) => handleChange('sample_po_date', e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                )}
              </div>
              {/* Criteria for Approval Text */}
              <div className="mt-4 pt-3 border-t border-slate-100">
                <Label className="text-xs font-medium text-slate-600 mb-1 block">Criteria for Approval — Additional Notes</Label>
                <textarea
                  value={formData.criteria_approval_text}
                  onChange={(e) => handleChange('criteria_approval_text', e.target.value)}
                  placeholder="Enter additional notes about the criteria for approval..."
                  className="w-full min-h-[60px] text-sm border border-slate-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-y"
                  data-testid="criteria-approval-text"
                />
              </div>
            </CardContent>
          </Card>
          )}

          {/* Scope of Approval — only for AS suppliers */}
          {formData.supplier_type !== 'NON-AS' && (
          <Card className={formData.scope_of_approval.length === 0 ? 'border-red-200' : ''}>
            <CardHeader>
              <CardTitle className="text-base">
                Scope of Approval <span className="text-red-500">*</span>
              </CardTitle>
              <CardDescription className="text-xs">
                Select applicable scopes for this supplier (mandatory - at least one required)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {formData.scope_of_approval.length === 0 && (
                <p className="text-xs text-red-500 mb-3">⚠️ Please select at least one scope of approval</p>
              )}
              <div className="flex flex-wrap gap-2 mb-4">
                {scopes.map(scope => (
                  <Badge
                    key={scope.id}
                    variant={formData.scope_of_approval.includes(scope.id) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-all ${
                      formData.scope_of_approval.includes(scope.id)
                        ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                        : 'hover:bg-slate-100'
                    }`}
                    onClick={() => handleScopeToggle(scope.id)}
                  >
                    {scope.name}
                    {formData.scope_of_approval.includes(scope.id) && (
                      <X className="w-3 h-3 ml-1" />
                    )}
                  </Badge>
                ))}
              </div>
              
              {/* Scope of Approval Text */}
              <div className="mt-4 pt-3 border-t border-slate-100">
                <Label className="text-xs font-medium text-slate-600 mb-1 block">Scope of Approval — Additional Notes</Label>
                <textarea
                  value={formData.scope_of_approval_text}
                  onChange={(e) => handleChange('scope_of_approval_text', e.target.value)}
                  placeholder="Enter additional notes about the scope of approval..."
                  className="w-full min-h-[60px] text-sm border border-slate-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-y"
                  data-testid="scope-approval-text"
                />
              </div>
              
              {/* Add new scope (only users who can edit metadata - admin, quality_manager, or manager, NOT app_user) */}
              {canEditMetadata('supplier_mgmt') && (
                <div className="flex gap-2 pt-3 border-t border-slate-100">
                  <Input
                    value={newScopeName}
                    onChange={(e) => setNewScopeName(e.target.value)}
                    placeholder="Add new scope category..."
                    className="h-8 text-xs flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddScope}
                    disabled={!newScopeName.trim()}
                    className="h-8 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* NON-AS info card */}
          {formData.supplier_type === 'NON-AS' && (
            <Card className="border-amber-200 bg-amber-50/30">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Badge className="bg-amber-100 text-amber-800 border-amber-300 shrink-0">NON-AS</Badge>
                  <div className="text-xs text-amber-800 space-y-1">
                    <p className="font-medium">General Purpose Supplier</p>
                    {isAdmin ? (
                      <p>This supplier will be auto-approved. No approval criteria, scope of approval, or reevaluation process is required.</p>
                    ) : (
                      <p>This supplier will be submitted for admin approval. Once approved, items from this supplier can be received without a Purchase Request or Purchase Order. No scope of approval or reevaluation is required.</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submit Button (mobile) */}
          <div className="md:hidden">
            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Supplier'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
